import React from "react";
import { getAuthData } from "../auth/session.js";
import "./daily-gift.css";
import { giftRewardView } from "./rewards.js";

async function request(operation, body = {}) {
  const response = await fetch(`/api/daily-gift/${operation}`, {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ initData: getAuthData(), ...body }), signal: AbortSignal.timeout(12000)
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || "Не удалось загрузить подарок.");
  return data;
}

function GiftBox() {
  return <svg className="daily-gift-box" viewBox="0 0 88 88" fill="none" aria-hidden="true">
    <defs>
      <linearGradient id="gift-body" x1="18" y1="35" x2="72" y2="77" gradientUnits="userSpaceOnUse"><stop stopColor="#7751A8"/><stop offset=".55" stopColor="#442762"/><stop offset="1" stopColor="#2B193F"/></linearGradient>
      <linearGradient id="gift-ribbon" x1="33" y1="15" x2="55" y2="77" gradientUnits="userSpaceOnUse"><stop stopColor="#FFDEA0"/><stop offset=".5" stopColor="#DFA75B"/><stop offset="1" stopColor="#9C653D"/></linearGradient>
    </defs>
    <ellipse cx="44" cy="75" rx="30" ry="6" fill="#B27EFF" opacity=".12"/>
    <path d="M25 36h38v31a5 5 0 0 1-5 5H30a5 5 0 0 1-5-5V36Z" fill="url(#gift-body)" stroke="#AE83D2" strokeWidth="1.2"/>
    <path d="M39 38h10v34H39z" fill="url(#gift-ribbon)"/>
    <rect x="21" y="31" width="46" height="11" rx="3" fill="#72509A" stroke="#C39AE1" strokeWidth="1.2"/>
    <path d="M39 31h10v11H39z" fill="url(#gift-ribbon)"/>
    <path d="M43 30c-10 0-16-3-15-9 1-6 9-5 15 9Zm2 0c10 0 16-3 15-9-1-6-9-5-15 9Z" fill="#69449A" stroke="#DCB7F5" strokeWidth="1.5"/>
    <path d="M43 29c-4-7-9-9-12-8m14 8c4-7 9-9 12-8" stroke="#F4CA79" strokeWidth="1.2" strokeLinecap="round"/>
    <circle cx="44" cy="30" r="3.5" fill="#F4CA79"/><path d="M30 51h4m-2-2v4m25 6h4m-2-2v4" stroke="#DEB4F6" strokeLinecap="round" opacity=".65"/>
  </svg>;
}

function OpenedReward({ name }) {
  const reward = giftRewardView(name);
  if (!reward) return <small>{name}</small>;
  return <>
    <img className="daily-gift-reward-image" src={reward.image} alt="" />
    <small><b>{reward.amount}</b> {reward.label}</small>
    {reward.jackpot && <span className="daily-gift-jackpot">Джекпот!</span>}
  </>;
}

export function DailyGiftModal({ onReward }) {
  const [state, setState] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState("");
  React.useEffect(() => {
    let active = true;
    if (getAuthData()) request("state").then(data => {
      if (active && (data.available || data.subscribed === false)) setState(data);
    }).catch(() => {});
    return () => { active = false; };
  }, []);
  React.useEffect(() => {
    if (!state) return;
    const previous = document.activeElement;
    const closeOnEscape = event => { if (event.key === "Escape") setState(null); };
    document.addEventListener("keydown", closeOnEscape);
    return () => { document.removeEventListener("keydown", closeOnEscape); previous?.focus?.(); };
  }, [!!state]);
  if (!state) return null;
  async function open(index) {
    if (busy || !state.attemptsLeft || state.opened[index]) return;
    setBusy(true); setError("");
    try {
      const data = await request("open", { sessionId: state.sessionId, index });
      setState(data); onReward?.();
    } catch (err) {
      setError(err.message);
      // Reload the committed session after a lost response or an opening in the bot.
      try { const fresh = await request("state"); if (fresh.sessionId) setState(fresh); } catch { /* Keep the field for retry. */ }
    } finally { setBusy(false); }
  }
  return <div className="daily-gift-overlay" onClick={() => !busy && setState(null)}>
    <div className="daily-gift-modal" role="dialog" aria-modal="true" aria-labelledby="daily-gift-title" onClick={e => e.stopPropagation()}>
      <button className="daily-gift-close" aria-label="Закрыть" onClick={() => setState(null)} disabled={busy}>×</button>
      <div className="daily-gift-ornament" aria-hidden="true"><span />✦<span /></div>
      <h2 id="daily-gift-title">Ежедневный подарок</h2>
      {state.subscribed === false ? <><p>Подпишись на канал, чтобы получать ежедневные подарки.</p><a className="daily-gift-subscribe" href="https://t.me/MetaCardOFF" target="_blank" rel="noreferrer">Подписаться</a><button className="daily-gift-recheck" onClick={async () => { try { const data = await request("state"); if (data.available) setState(data); else if (data.subscribed === false) setError("Подписка пока не найдена."); else setState(null); } catch (err) { setError(err.message); } }}>Проверить подписку</button></> : <>
        <p>{state.attemptsLeft ? "Выбери подарок и открой награду" : "Все подарки на сегодня открыты"}</p>
        <div className="daily-gift-count"><span className="daily-gift-count-icon" aria-hidden="true">✧</span> Осталось открытий <b>{state.attemptsLeft}</b></div>
        <div className="daily-gift-grid">{Array.from({length: 9}, (_, i) => <button key={i} className={state.opened[i] ? "daily-gift-tile is-opened" : "daily-gift-tile"} disabled={busy || !state.attemptsLeft || !!state.opened[i]} onClick={() => open(i)} aria-label={state.opened[i] ? `Открыто: ${state.opened[i]}` : `Открыть подарок ${i + 1}`}><span className="daily-gift-tile-number" aria-hidden="true">{String(i + 1).padStart(2, "0")}</span>{state.opened[i] ? <OpenedReward name={state.opened[i]} /> : <GiftBox />}</button>)}</div>
        <div className="daily-gift-footer"><span aria-hidden="true">✧</span> Новые подарки в <strong>00:00 МСК</strong></div>
      </>}
      {error && <p role="alert" className="daily-gift-error">{error}</p>}
    </div>
  </div>;
}